// Centralized runtime/config values for local testing and prod fallbacks.
// Effect: tweak behavior in one file without changing feature code.
import { getChatOrchestrationLegacyConfig } from './helpers/ai/aiConfigRuntimeHelpers';

// Helper for time-based settings.
// Effect: controls conversion for all `*_MS` thresholds below.
const time = (value, unit = 'milliseconds') => {
  const v = Number(value) || 0;
  const u = String(unit || '').toLowerCase().trim();
  switch (u) {
    case 'minutes':
    case 'minute':
      return Math.trunc(v * 60 * 1000);
    case 'hours':
    case 'hour':
      return Math.trunc(v * 60 * 60 * 1000);
    case 'days':
    case 'day':
      return Math.trunc(v * 24 * 60 * 60 * 1000);
    case 'ms':
    case 'millisecond':
    case 'milliseconds':
    default:
      return Math.trunc(v);
  }
};

// See VERSIONS_IMAGES_PURGING.md for rationale and tuning guidance on versioning/image settings.

const VERSIONING = {
  // Effect: lower = less history/storage, higher = more restore points.
  MAX_VERSIONS: 10,

  // Effect: longer = deleted images remain recoverable longer.
  PURGE_RETENTION_MS: time(60, 'minutes'),

  // Effect: lower = auto-versions created more frequently by save count.
  AUTO_VERSION_SAVE_INTERVAL: 5,

  // Effect: lower = time-based auto-versions happen sooner.
  AUTO_VERSION_TIME_INTERVAL_MS: time(15, 'minutes'),

  // Effect: higher = tiny edits are ignored; only larger text deltas count.
  AUTO_VERSION_MIN_OFFSET: 40,

  // Effect: true = image add/remove forces immediate version snapshot.
  AUTO_VERSION_ON_IMAGE_CHANGE: true

};

const IMAGE = {
  // Effect: limits upload resize width; lower reduces file size and detail.
  MAX_UPLOAD_WIDTH: 2000,
  // Effect: lower = more compression/smaller files, higher = better quality/larger files.
  UPLOAD_QUALITY: 0.85,
  // Effect: minimum rendered image width allowed in editor.
  MIN_NODE_WIDTH: 48,
  // Effect: maximum rendered image width allowed in editor.
  MAX_NODE_WIDTH: 2000
};

const SEARCH = {
  // Effect: caps max number of docs shown in search modal results.
  RESULT_LIMIT: 20,
  // Effect: minimum query length before RPC search runs.
  MIN_QUERY_LENGTH: 1,
  // Effect: false = search only on Enter/button; true = search while typing.
  RUN_ON_TYPE: true,
  // Effect: minimum trimmed characters required before typed search runs.
  TYPE_MIN_QUERY_LENGTH: 3,
  // Effect: delay before firing typed search when RUN_ON_TYPE is true.
  TYPE_DEBOUNCE_MS: 250,
  // Effect: default logical scope for API-shaped search options.
  DEFAULT_SCOPE: 'project',
  // Effect: active profile used by search UI calls (`SEARCH_UI` or `AI_CONTEXT`).
  ACTIVE_PROFILE: 'SEARCH_UI',
  // Effect: default style used by `apiSearchProjectDocs` compatibility wrapper.
  DEFAULT_DOC_SEARCH_STYLE: 'SEARCH_UI',
  // Effect: enables client-side post-filtering over RPC payload for quick FE refinements.
  ENABLE_FRONTEND_FILTERING: true,
  // Effect: RPC endpoint name used by `api/search.js`.
  RPC_NAME: 'search_project_docs',
  // Effect: true = search results include bounded content snippets and match metadata.
  RETURN_CONTENT: false,
  // Effect: default snippet length returned when `RETURN_CONTENT` or `options.returnContent` is enabled.
  CONTENT_SNIPPET_LENGTH: 100,
  // Effect: desired relative position of the match in snippet window (0.3 = 30% into snippet).
  CONTENT_SNIPPET_MATCH_TARGET: 0.3,
  // Effect: max snippet rows returned per result (typically one per matched chunk).
  CONTENT_SNIPPETS_PER_RESULT: 3,
  // Effect: hard cap for caller-provided snippet lengths.
  CONTENT_SNIPPET_MAX_LENGTH: 1200,
  // Effect: markers inserted around matched snippet terms in `content_marked`.
  MATCH_MARKER_START: '[[MATCH_START]]',
  MATCH_MARKER_END: '[[MATCH_END]]',
  // Weight reference (kept here for profile tuning):
  // NAME_EXACT: exact doc name match boost (strongest title signal).
  // NAME_PREFIX: doc name starts-with query boost.
  // NAME_TRGM: fuzzy doc name similarity boost via `pg_trgm`.
  // MD_EXACT: exact markdown substring match boost (strongly favors exact body hits).
  // MD_TRGM: fuzzy markdown content similarity boost via `pg_trgm`.
  // MD_FTS: full-text relevance boost via `tsvector`/`ts_rank_cd`.
  // HEADER_HIT: extra boost when query matches markdown heading lines.
  // Effect: predefined weighting/mode profiles to switch between UI search and AI retrieval.
  PROFILES: {
    SEARCH_UI: {
      // Effect: response shape expected by UI. `docs` returns one row per doc (with best/top chunks attached).
      RESULT_TYPE: 'docs',
      // Effect: doc score composition strategy.
      // - `max`: use only the single best chunk score for each doc.
      // - `aggregate`: sum the top-k chunk scores for each doc.
      // For "best doc" retrieval where evidence spread across multiple chunks should win, use `aggregate`.
      RANK_MODE: 'aggregate',
      // Effect: hard cap on number of rows returned for this profile.
      RESULT_LIMIT: 20,
      // Effect: minimum total score required for inclusion; raise to hide weak/zero matches.
      MIN_SCORE: 0.001,
      // Effect: when `RANK_MODE = aggregate`, number of top chunks included in summed score.
      // Larger values favor broader docs; smaller values favor a few strongest chunks.
      AGGREGATE_TOP_K: 5,
      // Effect: ranking signal weights used by SQL scoring for this profile.
      WEIGHTS: {
        NAME_EXACT: 100,
        NAME_PREFIX: 90,
        NAME_TRGM: 5,
        MD_EXACT: 60,
        MD_TRGM: 50,
        MD_FTS: 10,
        HEADER_HIT: 20
      }
    },
    AI_CONTEXT: {
      // Effect: response shape for retrieval pipelines. `chunks` returns one row per chunk.
      RESULT_TYPE: 'chunks',
      // Effect: in chunk mode this mainly impacts doc-level rollups/metadata and future reuse.
      RANK_MODE: 'aggregate',
      // Effect: cap for candidate chunks returned to AI retrieval pipeline.
      RESULT_LIMIT: 30,
      // Effect: keep at 0 for recall-heavy retrieval unless you need stricter filtering.
      MIN_SCORE: 0,
      // Effect: top-k chunks considered when aggregate logic is applied.
      AGGREGATE_TOP_K: 3,
      // Effect: retrieval-oriented weight mix (more semantic/body relevance, less title bias).
      WEIGHTS: {
        NAME_EXACT: 12,
        NAME_PREFIX: 8,
        NAME_TRGM: 10,
        MD_EXACT: 40,
        MD_TRGM: 12,
        MD_FTS: 18,
        HEADER_HIT: 14
      }
    },
    DOC_NAME_EXACT: {
      // Effect: title-only ranking profile for exact-name style experiments.
      RESULT_TYPE: 'docs',
      // Effect: exact single-hit ranking (no chunk aggregation).
      RANK_MODE: 'max',
      // Effect: default cap for exact-name style queries.
      RESULT_LIMIT: 20,
      // Effect: keep zero floor so exact-name filtering decides inclusion.
      MIN_SCORE: 0,
      // Effect: unused in `max` mode but kept for profile consistency.
      AGGREGATE_TOP_K: 1,
      // Effect: title-only signal weights for exact matching.
      WEIGHTS: {
        NAME_EXACT: 100,
        NAME_PREFIX: 0,
        NAME_TRGM: 0,
        MD_EXACT: 0,
        MD_TRGM: 0,
        MD_FTS: 0,
        HEADER_HIT: 0
      }
    }
  },
  // Effect: style registry for doc search entry points. Add new styles here.
  STYLES: {
    SEARCH_UI: {
      STRATEGY: 'PROFILE',
      PROFILE: 'SEARCH_UI'
    },
    DOC_NAME_EXACT: {
      // Effect: fast exact doc-name lookup (case-insensitive) via direct DB path.
      STRATEGY: 'DIRECT_DOC_NAME_EXACT',
      PROFILE: 'DOC_NAME_EXACT'
    }
  },
  // Effect: unified name-search config used by client-side name lookups
  NAME_SEARCH: {
    // Effect: number of results returned by client-side name lookup
    RESULT_LIMIT: 20,
    // Effect: trigram/fuzzy similarity threshold (0-1) for inclusion as a fuzzy match
    FUZZY_THRESHOLD: 0.25,
    // Effect: whether to include fuzzy matches when exact/prefix/contains are present
    INCLUDE_FUZZY_IF_STRONGER: true
  }
};

const CHUNK = {
  MIN_CHARS: 500,
  TARGET_CHARS: 800,
  MAX_CHARS: 1500,
  HEADER_LEVELS: [1, 2, 3, 4, 5, 6],
  VALIDATE_AFTER_WRITE: true
};

// Authoritative AI orchestration settings live in src/ai/aiConfig.js.
// This adapter only exposes legacy CONFIG.AI.CHAT_ORCHESTRATION keys used by existing callers/tests.
const chatOrchestration = getChatOrchestrationLegacyConfig();

const AI = {
  CHAT_ORCHESTRATION: chatOrchestration
};

const CONFIG = {
  // Effect: all versioning and auto-version behavior knobs.
  VERSIONING,
  // Effect: upload and editor image sizing/compression knobs.
  IMAGE,
  // Effect: search limits, scoring model, and RPC mapping.
  SEARCH,
  // Effect: chunking thresholds and save-time validation controls.
  CHUNK,
  // Effect: AI orchestration rollout and tuning knobs.
  AI,
  // Effect: shared utility for converting relative time values to ms.
  time
};

export default CONFIG;
